import { useState, useEffect, useCallback } from 'react'
import {
  getShops,
  getShop,
  verifyShop,
  suspendShop,
  unsuspendShop,
  closeShop,
  type ShopSummary,
  type ShopDetail,
  type ShopFilters,
} from '../../services/adminApi'
import { ShopCard } from '../../components/cards/ShopCard'
import { ShopDetailModal } from './ShopDetailModal'
import { CustomButton } from '../../components/common/CustomButton'
import { CustomTextInput } from '../../components/common/CustomTextInput'
import { useDebouncedValue } from '../../hooks/useDebouncedValue'

const statusOptions = [
  { value: '', label: 'All Status' },
  { value: 'pending', label: 'Pending' },
  { value: 'verified', label: 'Verified' },
  { value: 'suspended', label: 'Suspended' },
  { value: 'closed', label: 'Closed' },
]

export function Shops() {
  const [shops, setShops] = useState<ShopSummary[]>([])
  const [total, setTotal] = useState(0)
  const [page, setPage] = useState(1)
  const [totalPages, setTotalPages] = useState(1)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const [filters, setFilters] = useState<ShopFilters>({
    status: '',
    search: '',
    page: 1,
    page_size: 20,
  })

  const [searchInput, setSearchInput] = useState('')
  const debouncedSearch = useDebouncedValue(searchInput, 300)

  useEffect(() => {
    setFilters((prev) => (prev.search === debouncedSearch ? prev : { ...prev, search: debouncedSearch, page: 1 }))
  }, [debouncedSearch])

  const [selectedShop, setSelectedShop] = useState<ShopDetail | null>(null)

  const loadShops = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const response = await getShops(filters)
      if (response.ok && response.data) {
        setShops(response.data.items)
        setTotal(response.data.total)
        setPage(response.data.page)
        setTotalPages(response.data.total_pages)
      } else {
        setError(response.error?.message || 'Failed to load shops')
      }
    } catch {
      setError('Failed to connect to API')
    } finally {
      setLoading(false)
    }
  }, [filters])

  useEffect(() => {
    loadShops()
  }, [loadShops])

  const handleFilterChange = (key: keyof ShopFilters, value: string) => {
    setFilters((prev) => ({ ...prev, [key]: value, page: 1 }))
  }

  const handlePageChange = (newPage: number) => {
    setFilters((prev) => ({ ...prev, page: newPage }))
  }

  const handleOpenDetail = async (shop: ShopSummary) => {
    const response = await getShop(shop.id)
    if (response.ok && response.data) {
      setSelectedShop(response.data)
    }
  }

  const handleVerify = async (notes?: string) => {
    if (!selectedShop) return
    const response = await verifyShop(selectedShop.id, notes)
    if (response.ok && response.data) {
      setSelectedShop(response.data)
      loadShops()
    }
  }

  const handleSuspend = async (reason: string) => {
    if (!selectedShop) return
    const response = await suspendShop(selectedShop.id, reason)
    if (response.ok && response.data) {
      setSelectedShop(response.data)
      loadShops()
    }
  }

  const handleUnsuspend = async (notes?: string) => {
    if (!selectedShop) return
    const response = await unsuspendShop(selectedShop.id, notes)
    if (response.ok && response.data) {
      setSelectedShop(response.data)
      loadShops()
    }
  }

  const handleClose = async (reason: string) => {
    if (!selectedShop) return
    const response = await closeShop(selectedShop.id, reason)
    if (response.ok && response.data) {
      setSelectedShop(response.data)
      loadShops()
    }
  }

  return (
    <div>
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h2 className="font-heading text-2xl font-bold text-gray-900">Shops</h2>
          <p className="text-sm text-gray-500">{total} total shops</p>
        </div>
      </div>

      {/* Filters */}
      <div className="mb-6 flex flex-wrap gap-4">
        <div className="w-64">
          <CustomTextInput
            label=""
            placeholder="Search shops..."
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
          />
        </div>
        <select
          value={filters.status || ''}
          onChange={(e) => handleFilterChange('status', e.target.value)}
          className="rounded-xl border border-gray-300 px-4 py-2 text-sm focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
        >
          {statusOptions.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
      </div>

      {/* Error */}
      {error && (
        <div className="mb-6 rounded-xl bg-red-50 p-4 text-red-700">
          {error}
        </div>
      )}

      {/* Shops Grid */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
        </div>
      ) : shops.length === 0 ? (
        <div className="text-center py-12 text-gray-500">
          No shops found
        </div>
      ) : (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {shops.map((shop) => (
            <ShopCard key={shop.id} shop={shop} onClick={handleOpenDetail} />
          ))}
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="mt-6 flex items-center justify-center gap-2">
          <CustomButton
            onClick={() => handlePageChange(page - 1)}
            disabled={page <= 1}
            variant="secondary"
          >
            Previous
          </CustomButton>
          <span className="text-sm text-gray-600">
            Page {page} of {totalPages}
          </span>
          <CustomButton
            onClick={() => handlePageChange(page + 1)}
            disabled={page >= totalPages}
            variant="secondary"
          >
            Next
          </CustomButton>
        </div>
      )}

      {/* Detail Modal */}
      {selectedShop && (
        <ShopDetailModal
          shop={selectedShop}
          onClose={() => setSelectedShop(null)}
          onVerify={handleVerify}
          onSuspend={handleSuspend}
          onUnsuspend={handleUnsuspend}
          onClose_={handleClose}
        />
      )}
    </div>
  )
}
